


import { useEffect, useRef, useState } from "react";
import './tagOverflow.css'
/**
 * TagOverflow robusto:
 * - mide anchos en un contenedor offscreen invisible
 * - tiene en cuenta gap entre items
 * - re-calcula tras ResizeObserver, resize, cambios en tags y carga de fuentes
 */
export const TagOverflow = ({ tags = [], gap = 6 }) => {
  const containerRef = useRef(null);
  const measureRef = useRef(null); // contenedor offscreen para medir
  const [visibleCount, setVisibleCount] = useState(tags.length);

  // función que calcula cuántos tags entran
  const recalc = () => {
    const container = containerRef.current;
    const measureBox = measureRef.current;
    if (!container || !measureBox) return;

    // ancho disponible real del contenedor visible
    const containerWidth = Math.floor(container.getBoundingClientRect().width);

    // tomar ancho de cada tag medido en el measureBox (ya renderados allí)
    const tagEls = Array.from(measureBox.querySelectorAll(".tag-measure-item"));
    const ellipsisEl = measureBox.querySelector(".tag-measure-ellipsis");
    const ellipsisWidth = ellipsisEl ? Math.ceil(ellipsisEl.getBoundingClientRect().width) : 0;

    let total = 0;
    let count = 0;

    for (let i = 0; i < tagEls.length; i++) {
      const el = tagEls[i];
      const rect = el.getBoundingClientRect();
      const w = Math.ceil(rect.width);

      // si vamos a mostrar este tag, hay que sumar gap si no es el primero
      const gapAccum = count > 0 ? gap : 0;
      // comprobamos si cabe contando la posible "..." si no son todos
      // supondremos inicialmente que habrá overflow si i < tags.length - 1
      const remainingWouldExist = i < tagEls.length - 1;

      // si faltara espacio para este tag, se considera no entrar
      if (total + gapAccum + w > containerWidth) {
        break;
      }

      total += gapAccum + w;
      count++;
    }

    // si no entran todos, hay que asegurar espacio para el "..."
    if (count < tags.length) {
      // si ya no hay lugar para el ellipsis, quitamos un tag para dejarle espacio
      if (total + (count > 0 ? gap : 0) + ellipsisWidth > containerWidth) {
        count = Math.max(0, count - 1);
      }
    }

    setVisibleCount(count);
  };

  useEffect(() => {
    // Re-measure cuando:
    // - cambia tamaño del contenedor (ResizeObserver)
    // - cambia el tamaño de la ventana (fallback)
    // - cuando las fonts se cargan (document.fonts.ready)
    const container = containerRef.current;

    const rafMeasure = () => requestAnimationFrame(recalc);
    const observer = new ResizeObserver(() => rafMeasure());
    if (container) observer.observe(container);

    // fallback global
    window.addEventListener("resize", rafMeasure);

    // re-measure cuando las fuentes se carguen (fonts API)
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        rafMeasure();
      });
    }

    // también re-medimos si el array de tags cambió (se maneja por dependencia)
    // inicial
    rafMeasure();

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", rafMeasure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags, gap]);

  // Recalcular cuando el contenido del measureRef cambie (p. ej. imágenes o textos dinámicos)
  useEffect(() => {
    // Observamos mutaciones en el measure box (por si cambian textos)
    const measureBox = measureRef.current;
    if (!measureBox) return;
    const mo = new MutationObserver(() => requestAnimationFrame(recalc));
    mo.observe(measureBox, { childList: true, subtree: true, characterData: true });
    return () => mo.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags]);

  const shown = tags.slice(0, visibleCount);
  const isOverflow = visibleCount < tags.length;

  return (
    <>
      {/* Contenedor visible */}
      <div
        ref={containerRef}
        className="tag-container"
        style={{
          gap: `${gap}px`,
        }}
        aria-hidden={false}
      >
        {shown.map((p) => (
          <span
            key={p.id}
            className="tag-chip"
          >
            {p.name}
          </span>
        ))}

        {isOverflow && (
          <span
            className="tag-chip-ellipsis"
          >
            …
          </span>
        )}
      </div>

      {/* Contenedor offscreen para medir anchos reales; no es visible ni interfiere */}
      <div
        ref={measureRef}
        aria-hidden="true"
        role="none"
        className="offscreen-tag-container"
        
      >
        {tags.map((p) => (
          <span
            key={p.id}
            className="tag-measure-item"
          >
            {p.name}
          </span>
        ))}

        {/* medir el ancho del "..." también */}
        <span
          className="tag-measure-ellipsis"
          
        >
          …
        </span>
      </div>
    </>
  );
};
